import { Box, Button, Theme, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import { navigate } from "@patched/hookrouter";
import React, { useState } from "react";

import HeroLayoutWithTabs from "../../layout/HeroLayoutWithTabs/HeroLayoutWithTabs";
import useAnalyticsPage, {
  PageType,
} from "../../lib/analytics/useAnalyticsPage";
import { learnerRoutes } from "../../lib/Routes";
import { useQuizWriterExampleTopics } from "../Quiz/decorativeText";

export default function LearnerPlay() {
  const { t } = useTranslation("learnerApp\\LearnerPlay");
  useAnalyticsPage({ pageName: PageType.Discovery });
  const isQuizEnabled = useFeatureFlag("aic-quiz");

  const currentPath = isQuizEnabled
    ? learnerRoutes.playPath()
    : learnerRoutes.discoveryPath();

  return (
    <HeroLayoutWithTabs currentPath={currentPath}>
      <Box
        flex
        sx={{
          flexDirection: "column",
          gap: "16px",
          marginBottom: "4em",
        }}
      >
        <Typography variant="h2">
          {t("How would you like to play today?")}
        </Typography>
        <Typography variant="body1" sx={{ color: "grey.600" }}>
          {t("Start with a topic for a quiz, or a surprise idea.")}
        </Typography>

        <Box
          flex
          sx={(theme: Theme) => ({
            flexDirection: "column",
            gap: "20px",
            marginTop: "8px",
            [theme.breakpoints.up("md")]: {
              gap: "24px",
            },
          })}
        >
          {isQuizEnabled && <QuizWriterCard />}

          <MixupMachineCard />
        </Box>
      </Box>
    </HeroLayoutWithTabs>
  );
}

function QuizWriterCard() {
  const { t } = useTranslation("learnerApp\\LearnerPlay");
  const exampleTopics = useQuizWriterExampleTopics(t);
  const [topicValue, setTopicValue] = useState("");

  const handleCreateQuiz = () => {
    const trimmed = topicValue.trim();
    if (!trimmed) {
      return;
    }
    navigate(learnerRoutes.quizPath(), false, { topic: trimmed });
  };

  return (
    <Box
      sx={(theme: Theme) => ({
        background:
          "linear-gradient(135deg, #6366f1 0%, #06b6d4 50%, #10b981 100%)",
        borderRadius: "16px",
        padding: "28px 32px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        [theme.breakpoints.down("sm")]: {
          padding: "20px 24px",
        },
      })}
    >
      <Box flex sx={{ alignItems: "center", gap: "16px" }}>
        <Box
          flex
          sx={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            flexShrink: 0,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "white" }}
            data-lov-id="src/pages/ExploreLandingV2Original.tsx:125:14"
            data-lov-name="PenLine"
            data-component-path="src/pages/ExploreLandingV2Original.tsx"
            data-component-line="125"
            data-component-file="ExploreLandingV2Original.tsx"
            data-component-name="PenLine"
            data-component-content="%7B%22className%22%3A%22w-5%20h-5%20text-white%22%7D"
          >
            <path d="M12 20h9"></path>
            <path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"></path>
          </svg>
        </Box>
        <Box flex sx={{ flexDirection: "column", gap: "4px" }}>
          <Typography
            variant="h4"
            sx={{ color: "common.white", fontWeight: 700 }}
          >
            {t("Quiz Writer")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255, 255, 255, 0.85)" }}
          >
            {t("Start with a topic and create a quiz to explore it.")}
          </Typography>
        </Box>
      </Box>

      <Box
        id="play-quiz-topic-input"
        component="input"
        value={topicValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTopicValue(e.target.value)
        }
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter") {
            handleCreateQuiz();
          }
        }}
        placeholder={t("What should your quiz be about?")}
        aria-label={t("Quiz topic")}
        sx={{
          width: "100%",
          height: "48px",
          padding: "12px 16px",
          fontSize: "1.5rem",
          borderRadius: "12px",
          border: "2px solid rgba(255, 255, 255, 0.4)",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          outline: "none",
          fontFamily: "inherit",
          boxSizing: "border-box",
          color: "#1a1a2e",
          "&::placeholder": {
            color: "#9ca3af",
          },
          "&:focus": {
            borderColor: "rgba(255, 255, 255, 0.8)",
            backgroundColor: "common.white",
          },
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "1.35rem",
            color: "rgba(255, 255, 255, 0.9)",
            marginRight: "4px",
          }}
        >
          {t("Try")}
        </Typography>
        {exampleTopics.map(example => (
          <Box
            key={example}
            onClick={() => setTopicValue(example)}
            sx={{
              fontWeight: 600,
              borderRadius: "999px",
              border: "1px solid rgba(255, 255, 255, 0.35)",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "common.white",
              padding: "5px 14px",
              fontSize: "1.3rem",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": {
                border: "1px solid rgba(255, 255, 255, 0.5)",
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            {example}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={handleCreateQuiz}
          disabled={!topicValue.trim()}
          variant="contained"
          color="primary"
          sx={{ fontWeight: 800, fontSize: "1.6rem", padding: "8px 21px" }}
        >
          {t("Create quiz →")}
        </Button>
      </Box>
    </Box>
  );
}

function MixupMachineCard() {
  const { t } = useTranslation("learnerApp\\LearnerPlay");
  const goToMixUpMachine = () => navigate(learnerRoutes.mixUpMachinePath());

  const sampleCombinations = [t("Coding + Music"), t("Science + Gaming")];

  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        background:
          "linear-gradient(135deg, #f59e0b 0%, #ef4444 50%, #ec4899 100%)",
        borderRadius: "16px",
        padding: "28px 32px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        [theme.breakpoints.down("sm")]: {
          padding: "20px 24px",
        },
      })}
    >
      <Box flex sx={{ alignItems: "center", gap: "16px" }}>
        <Box
          flex
          sx={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            flexShrink: 0,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "white" }}
            data-lov-id="src/pages/ExploreLandingV2Original.tsx:255:18"
            data-lov-name="ShuffleIcon"
            data-component-path="src/pages/ExploreLandingV2Original.tsx"
            data-component-line="255"
            data-component-file="ExploreLandingV2Original.tsx"
            data-component-name="ShuffleIcon"
            data-component-content="%7B%22className%22%3A%22w-5%20h-5%20text-white%22%7D"
          >
            <path d="m18 14 4 4-4 4"></path>
            <path d="m18 2 4 4-4 4"></path>
            <path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22"></path>
            <path d="M2 6h1.972a4 4 0 0 1 3.6 2.2"></path>
            <path d="M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45"></path>
          </svg>
        </Box>
        <Box flex sx={{ flexDirection: "column", gap: "4px" }}>
          <Typography
            variant="h4"
            sx={{ color: "common.white", fontWeight: 700 }}
          >
            {t("Mix-Up Machine")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255, 255, 255, 0.85)" }}
          >
            {t("Select 2 topics and mix them to explore your interests.")}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "1.35rem",
            color: "rgba(255, 255, 255, 0.9)",
            marginRight: "4px",
          }}
        >
          {t("Try")}
        </Typography>
        {sampleCombinations.map(label => (
          <Box
            key={label}
            component="button"
            type="button"
            onClick={goToMixUpMachine}
            sx={{
              fontWeight: 600,
              borderRadius: "999px",
              border: "1px solid rgba(255, 255, 255, 0.35)",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "common.white",
              padding: "5px 14px",
              fontSize: "1.3rem",
              cursor: "pointer",
              userSelect: "none",
              fontFamily: "inherit",
              lineHeight: 1.3,
              "&:hover": {
                border: "1px solid rgba(255, 255, 255, 0.5)",
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={goToMixUpMachine}
          variant="contained"
          color="primary"
          sx={{ fontWeight: 800, fontSize: "1.6rem", padding: "8px 21px" }}
        >
          {t("Start Mixing →")}
        </Button>
      </Box>
    </Box>
  );
}
